import {executeExternalScript} from "./execute-external-script.ts";

/**
 * Execute an external script, and return a Promise to a variable it creates.
 *
 * Generally, we vendor third-party dependencies, but some dependencies
 * cannot be vendored. For example, Stripe does not allow for vendoring since
 * they want to update it continuously, and MathJax does not bundle easily.
 * In these cases, this provides an escale hatch to access unvendored scripts.
 *
 * url is the URL of a script that, when executed, stores the global variable
 * with the name globalName on the window object. The returned Promise
 * resolves to that global variable. If the global variable already exists, we
 * return it immediately without making a request.
 *
 * You can call this as many times as you want. It only loads the script the
 * first time. The same promise will be returned in subsequent calls.
 */
export function importFromExternalScript<T>(
    url: string,
    globalName?: string,
): Promise<T | null | undefined> {
    const key = `${url}#${globalName || ""}`;

    return (MODULE_MEMO[key] =
        MODULE_MEMO[key] ||
        new Promise((resolve, reject) => {
            /**
             * TODO(WEB-2290, somewhatabstract): Don't reference window
             * directly.
             */
            if (globalName && window[globalName]) {
                resolve(window[globalName]);
            } else {
                executeExternalScript(url)
                    .then(() =>
                        resolve(globalName ? window[globalName] : undefined),
                    )
                    .catch((err) => reject(err));
            }
        }));
}

const MODULE_MEMO: Record<string, Promise<any>> = {};
